import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FONT, COLOR, CHIP_TONES } from "../lib/tokens";
import { pageTitle } from "../lib/mcmTokens";
import { useTheme as useMCMTheme } from "../lib/theme";
import MCMBigCard from "./MCMBigCard";
import WeeklyPlanner from "./planner/WeeklyPlanner";
// CookMode is mounted at the App level (see App.jsx cookModeRecipe
// state). This overlay no longer renders it inline — cook requests
// are bubbled up via onStartCook, which lets the resume banner keep
// the session alive across tab changes.
import CustomRecipeBuilder from "./CustomRecipeBuilder";
import AIRecipe from "./AIRecipe";
import AddRecipeFromSheet from "./AddRecipeFromSheet";
import SchedulePicker from "./SchedulePicker";
import {
  RECIPES,
  totalTimeMin,
  difficultyLabel,
  findRecipe,
} from "../data/recipes";
import { inferCanonicalFromName, findIngredient } from "../data/ingredients";
import { useUserRecipes } from "../lib/useUserRecipes";
import { useMeals } from "../lib/useMeals";
import { useScheduledMeals } from "../lib/useScheduledMeals";
import { useIngredientInfo } from "../lib/useIngredientInfo";
import { useCookLog } from "../lib/useCookLog";
import { useToast } from "../lib/toast";
import { mealNutrition, formatMacros } from "../lib/nutrition";
import { useBrandNutrition } from "../lib/useBrandNutrition";

// CreateMenu — full-screen overlay launched by the center ➕ in the
// tab bar. Universal creation hub: launches either a COOK flow
// (custom / AI / template recipe → CookMode) or dispatches a PANTRY
// action (scan / manual add) back to the Kitchen tab via
// onRequestPantryAction.
//
// Drives a small state machine:
//   choose     → four big cards (CUSTOM / AI / TEMPLATE / ADD TO PANTRY)
//   custom     → multi-step recipe builder, saves to user_recipes
//   ai         → Claude-drafted recipe from the pantry
//   template   → browseable picker over YOUR RECIPES + AI DRAFTS + bundled
//   (scan / manual promoted to the main chooser; no sub-phase needed)
//   cook       → hands the resolved recipe to CookMode
// (orthogonal) scheduling → SchedulePicker overlaid on any mode
//
// Save / schedule / cook all persist through useUserRecipes.saveRecipe.
// Privacy rule: recipes are private by default; the schedule path flips
// shared=true since scheduled meals are family-visible.

export default function CreateMenu({
  userId, profile, familyKey,
  // Pantry-off mode (profiles.kitchen_tracking === false): AI drafts
  // prompt-first with no pantry context, and pantry summary chrome
  // hides. The add-to-kitchen ingress stays reachable (borderline
  // off, not amputated).
  kitchenTracking = true,
  pantry, setPantry, shoppingList, setShoppingList, onGoToShopping,
  family = [], friends = [],
  onClose, onCooked,
  onRequestPantryAction,
  onStartCook,
  // Background final-pass generation. App mints a pending Recipe-Book
  // stub, navigates to it, and runs the slow Sonnet pass detached so
  // the user is never stuck on a spinner. Passed straight to AIRecipe.
  onBackgroundGenerate,
  // Direct routes from the choose phase that bypass the legacy
  // sub-flows. Each handler in App closes the create menu before
  // opening its target so we don't end up with two overlays stacked.
  //   onOpenRecipeBook      — "Pick a recipe or meal" → Recipe Book TOC
  //   onOpenAddRecipeSheet  — "Write your own"        → Recipe Book + paste sheet
  //   onOpenAddDraft        — "Add by hand"           → MCMAddDraftSheet
  onOpenRecipeBook,
  onOpenAddRecipeSheet,
  onOpenAddDraft,
  onOpenEatQuick,
  // External photo-mode seed handoff. RecipeBook's `+` opens the same
  // AddRecipeFromSheet hub; when its photo flow finishes identifying
  // items, the seed comes back through App and lands here. We mirror
  // CreateMenu's own internal photo flow: stash as aiPantryOverride,
  // jump to AI mode, then call onAiSeedConsumed so the parent can
  // clear its holding state.
  initialAiSeed,
  initialAiSketch,
  onAiSeedConsumed,
  // iOS Share-extension handoff. When iOS forwards a TikTok / Instagram
  // / YouTube URL via the share sheet, App stashes it here and we open
  // AddRecipeFromSheet pre-filled in LINK mode the moment we mount.
  initialSharedRecipeUrl = null,
  onSharedRecipeUrlConsumed,
}) {
  // AIRecipe → "ADD TO PANTRY" handler. Opens the manual add-item
  // drawer pre-seeded with the recipe's ingredient identity. Lets
  // the user log "I just bought nutmeg" without routing through the
  // shopping-list workflow. Reuses onOpenAddDraft (the existing
  // hand-add path) — App.jsx wires it to setMcmAddDraft.
  // Match the canonical add-flow entry point used by App.jsx's
  // kitchen ADD button (`setMcmAddDraft({ mode: "blank" })`). The
  // canonical+name+amount fields below are seed data that
  // AddItemSheet picks up via setCanonical on first mount —
  // EntryGate still surfaces (Scan / Wizard / Manual / Photo
  // picker), but the identity is already bound.
  const onAIAddToPantry = onOpenAddDraft
    ? (item) => onOpenAddDraft({
        mode: "blank",
        name:        item?.name || null,
        canonicalId: item?.ingredientId || null,
        initialAmount: item?.amount || null,
      })
    : null;
  // `cook` was once a valid mode here when CookMode rendered inline.
  // That's gone now — cook requests exit through onStartCook + onClose.
  const { theme: mcmTheme } = useMCMTheme();
  const [mode, setMode] = useState("choose");        // choose | custom | ai | template
  // When set, the AI sub-phase replaces the user's actual pantry with
  // a photo-derived seed (output of AddRecipeFromSheet's PHOTO mode).
  // Lets the user draft a recipe around exactly what was in the photo
  // instead of their full kitchen. Cleared when the AI sub-phase exits.
  const [aiPantryOverride, setAiPantryOverride] = useState(null);
  // Composed meal-prompt seed from finished_dish photo mode. Reads
  // dishName + techniques + finishing from the sketch and turns them
  // into a one-liner AIRecipe pre-fills its meal prompt with. Cleared
  // alongside aiPantryOverride.
  const [aiMealPromptSeed, setAiMealPromptSeed] = useState(null);
  // Local hold of the finished_dish sketch. App.jsx clears its
  // pendingAiPhotoSketch immediately after handoff, so we keep our
  // own copy to feed AIRecipe → AIRecipeSetup for the pinned chip
  // strip ("FROM YOUR PHOTO" + techniques + sauces). Cleared alongside
  // aiPantryOverride.
  const [aiSketchOverride, setAiSketchOverride] = useState(null);

  // External photo-seed bootstrap. When App hands us a seed (from
  // RecipeBook's `+` photo flow), enter AI mode with the seed as
  // the working pantry, then notify the parent so it clears.
  useEffect(() => {
    if (!initialAiSeed) return;
    setAiPantryOverride(initialAiSeed);
    setAiSketchOverride(initialAiSketch || null);
    // Compose a meal-prompt seed from the finished_dish sketch so
    // Claude inherits the visible characteristics on first generate.
    if (initialAiSketch) {
      const parts = [];
      if (initialAiSketch.dishName) parts.push(initialAiSketch.dishName);
      const techs = Array.isArray(initialAiSketch.techniques)
        ? initialAiSketch.techniques.filter(Boolean)
        : [];
      if (techs.length > 0) parts.push(`Techniques: ${techs.join(", ")}`);
      // Sauces are first-class — Claude needs the specific name in the
      // meal prompt so a "Steak with chimichurri" doesn't draft as a
      // "Steak with green sauce." Use the model's best guess; the
      // user can edit the prompt before generating if it picked wrong.
      const sauces = Array.isArray(initialAiSketch.sauces)
        ? initialAiSketch.sauces
            .filter((s) => s && s.name)
            .map((s) => String(s.name).replace(/_/g, " "))
        : [];
      if (sauces.length > 0) parts.push(`Sauce: ${sauces.join(" + ")}`);
      const f = initialAiSketch.finishing;
      if (f && f.type && f.type !== "none") {
        parts.push(`Finishing: ${String(f.type).replace(/_/g, " ")}`);
      }
      const s = initialAiSketch.structure;
      if (s && s.layers) parts.push(`${s.layers}-layer`);
      if (initialAiSketch.plating) parts.push(`Plated: ${initialAiSketch.plating}`);
      // User's answers to the model's follow-up questions. These are
      // explicit corrections / disambiguations (Ganache vs Buttercream;
      // chimichurri vs salsa verde; medium-rare vs medium). Fold into
      // the prompt so Claude weights them above the model's original
      // ambiguous read. Format: "User confirmed: <answer1>, <answer2>".
      if (initialAiSketch.userAnswers && typeof initialAiSketch.userAnswers === "object") {
        const answered = Object.values(initialAiSketch.userAnswers)
          .filter((v) => typeof v === "string" && v.trim());
        if (answered.length > 0) {
          parts.push(`User confirmed: ${answered.join(", ")}`);
        }
      }
      setAiMealPromptSeed(parts.length > 0 ? parts.join(" · ") : null);
    } else {
      // Raw-ingredients mode (no sketch). Compose a constraining
      // prompt FROM the identified ingredients themselves so Claude
      // has a concrete brief instead of dream mode. Empty prompt +
      // generic pantry = the famous "Cacio e Pepe rut" — Claude's
      // modal answer when given nothing specific. Naming the actual
      // ingredients in the prompt makes it impossible to default-pivot.
      const seedItems = Array.isArray(initialAiSeed) ? initialAiSeed : [];
      if (seedItems.length > 0) {
        const names = seedItems
          .map((i) => i?.name)
          .filter((n) => typeof n === "string" && n.trim())
          .slice(0, 10);
        if (names.length > 0) {
          setAiMealPromptSeed(
            `Build a recipe around these specific ingredients I just photographed: ${names.join(", ")}. Use them directly — don't pivot to a standard pasta or default dish.`,
          );
        } else {
          setAiMealPromptSeed(null);
        }
      } else {
        setAiMealPromptSeed(null);
      }
    }
    setMode("ai");
    onAiSeedConsumed?.();
    // Intentional: only react to seed identity changes, not handler
    // identity churn from the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAiSeed, initialAiSketch]);
  // Whether AddRecipeFromSheet is open. Three input pipelines (photo /
  // link / paste-text) live inside that one sheet.
  const [addRecipeOpen, setAddRecipeOpen] = useState(false);
  // Weekly AI planner — full-screen overlay. No `schedule` prop here:
  // the Calendar tab is unmounted while Create is up, and the planner
  // falls back to the standalone insertScheduledMeal writer.
  const [plannerOpen, setPlannerOpen] = useState(false);
  // URL the sheet should pre-seed in LINK mode (one-shot, cleared
  // after the sheet consumes it). Threaded through from App when iOS
  // share-extension hands us a TikTok / Instagram / YouTube link.
  const [sharedLinkSeed, setSharedLinkSeed] = useState(null);
  useEffect(() => {
    if (!initialSharedRecipeUrl) return;
    setSharedLinkSeed(initialSharedRecipeUrl);
    setAddRecipeOpen(true);
    onSharedRecipeUrlConsumed?.();
  }, [initialSharedRecipeUrl, onSharedRecipeUrlConsumed]);
  // When set, the meal-detail overlay is shown (rendered on top of
  // the template picker) so the user can inspect pieces + cook one.
  const [viewingMeal, setViewingMeal] = useState(null);
  // When set, SchedulePicker renders on top of the current mode. The
  // recipe has already been persisted to user_recipes by the time we
  // enter this state — picking a day just writes the scheduled_meals row.
  const [scheduling, setScheduling] = useState(null); // recipe object | null

  const { recipes: userRecipes, saveRecipe, findBySlug: findUserRecipe } = useUserRecipes(userId);
  // MEAL composition hook — exposes hydrated meals + CRUD. Pieces
  // resolve through userRecipes and bundled RECIPES so tap-to-cook on
  // a meal's piece hands CookMode a full recipe object.
  const { meals, createMeal, deleteMeal } = useMeals(userId, {
    userRecipes,
    bundledRecipes: RECIPES,
  });
  const { schedule } = useScheduledMeals(userId, {
    recipeResolver: (slug, ownerUserId) => findRecipe(slug, findUserRecipe, ownerUserId),
  });
  const { push: pushToast } = useToast();
  // Ingredient enrichment is provided once at App level; reading it
  // here lets AIRecipe's context builder look up flavorProfile/pairs/
  // diet per pantry item without re-fetching.
  const ingredientInfo = useIngredientInfo();
  // Brand-nutrition lookup — wrapped in a Map-like shape for the
  // resolver in nutrition.js. Shared by the MealDetail totals card
  // and anywhere else in this overlay that renders macros.
  const { get: getBrandNutrition } = useBrandNutrition();
  const brandNutrition = useMemo(
    () => ({ get: (k) => getBrandNutrition?.(k) || null }),
    [getBrandNutrition],
  );
  // Cook logs feed the AI-context "recent history" summary. Lazily
  // loaded at the CreateMenu level so users who never open the overlay
  // don't pay for the subscription. Cookbook's own useCookLog call
  // dedupes via supabase channel naming, so a second subscriber here
  // costs roughly nothing.
  const { logs: cookLogs = [] } = useCookLog(userId, familyKey);

  const userName = profile?.name?.trim().split(/\s+/)[0] || null;
  const hasFamily = family.length > 0;

  // Template picker — search over both user recipes AND bundled. The
  // three sections (CUSTOM · AI · BUNDLED) stay visually distinct so
  // the user can tell what they're tapping even without reading the
  // header, but the search box filters all three together.
  const [query, setQuery] = useState("");

  // Split user recipes by source; already sorted newest-first by the hook.
  const userCustom = useMemo(
    () => userRecipes.filter(r => r.source === "custom" && r.userId === userId),
    [userRecipes, userId],
  );
  const userAI = useMemo(
    () => userRecipes.filter(r => r.source === "ai" && r.userId === userId),
    [userRecipes, userId],
  );
  // Family-shared recipes authored by someone ELSE. Migration 0052's
  // RLS only exposes other family members' user_recipes rows when
  // their `shared` column is true, so this filter is tight by design:
  // a family member's private draft never leaks into the viewer's
  // picker. These are the rows the previous build was silently
  // dropping — hence "I shared it but my other account can't see it
  // as an option to cook."
  const familyShared = useMemo(
    () => userRecipes.filter(r => r.userId !== userId && r.shared && r.recipe),
    [userRecipes, userId],
  );
  // userId → first name, used to tag each family row in the picker
  // so the viewer knows whose recipe they're looking at (e.g.,
  // "YOURS" vs "MARISSA"). Unknown authors fall through to a
  // neutral "FAMILY" label.
  const authorNameFor = useMemo(() => {
    const map = new Map();
    for (const f of family || []) {
      if (f?.otherId && f?.other?.name) {
        map.set(f.otherId, String(f.other.name).trim().split(/\s+/)[0]);
      }
    }
    return (id) => map.get(id) || "FAMILY";
  }, [family]);

  const matches = (r, q) => {
    if (!q) return true;
    return (r.title || "").toLowerCase().includes(q) ||
           (r.cuisine || "").toLowerCase().includes(q) ||
           (r.category || "").toLowerCase().includes(q);
  };
  const filteredUserCustom = useMemo(() => {
    const q = query.trim().toLowerCase();
    return userCustom.filter(ur => matches(ur.recipe, q));
  }, [userCustom, query]);
  const filteredUserAI = useMemo(() => {
    const q = query.trim().toLowerCase();
    return userAI.filter(ur => matches(ur.recipe, q));
  }, [userAI, query]);
  const filteredFamilyShared = useMemo(() => {
    const q = query.trim().toLowerCase();
    return familyShared.filter(ur => matches(ur.recipe, q));
  }, [familyShared, query]);
  const filteredBundled = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? RECIPES.filter(r => matches(r, q)) : RECIPES;
  }, [query]);
  // Meals are searched against the meal's own identity (name /
  // cuisine / mealTiming) plus each piece's title, so typing "ribeye"
  // surfaces a meal whose main is named Ribeye even if the meal was
  // renamed to something generic.
  const filteredMeals = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return meals;
    return meals.filter(m => {
      if ((m.name || "").toLowerCase().includes(q)) return true;
      if ((m.cuisine || "").toLowerCase().includes(q)) return true;
      if ((m.mealTiming || "").toLowerCase().includes(q)) return true;
      return (m.pieces || []).some(p => (p.recipe?.title || "").toLowerCase().includes(q));
    });
  }, [meals, query]);

  // Hand the chosen recipe up to App and close the overlay. App
  // mounts CookMode at the top level so navigating tabs away from a
  // cook doesn't destroy the session — useActiveCookSession + the
  // CookBanner keep the in-flight cook visible and resumable.
  const startCooking = (recipe) => {
    onStartCook?.(recipe);
    onClose?.();
  };

  // Back from a nested mode should return to the chooser, not to the
  // parent — only "close" from the chooser itself actually closes.
  const backToChoose = () => {
    setMode("choose");
  };

  // Persist a builder/draft output, handling every callsite's error
  // expectations. `opts` carries { shared, submitForReview } from the
  // child's toggle state.
  const persist = async (recipe, source, opts = {}) => {
    try {
      return await saveRecipe(recipe, source, opts);
    } catch (e) {
      console.error(`[quickcook] ${source} save failed:`, e);
      pushToast("Couldn't save your recipe", { emoji: "⚠️", kind: "warn" });
      return null;
    }
  };

  // SAVE action — persist, toast, close the overlay. Used by both the
  // custom builder and the AI preview.
  const handleSave = (source) => async (recipe, opts = {}) => {
    const row = await persist(recipe, source, opts);
    if (!row) return;
    pushToast("Saved to your recipes", { emoji: "💾", kind: "info" });
    onClose?.();
  };

  // SCHEDULE action — persist with shared=true (scheduling is a family-
  // visible action), then open SchedulePicker on top of the current
  // mode. The picker writes the scheduled_meals row and closes.
  const handleSchedule = (source) => async (recipe, opts = {}) => {
    const row = await persist(recipe, source, { ...opts, shared: true });
    if (!row) return;
    // Use the stamped (db-slugged) recipe so scheduled_meals.recipe_slug
    // resolves via the user-recipe fallback in findRecipe.
    setScheduling(row.recipe);
  };

  // COOK IT — save then cook. Two rules learned the hard way:
  //
  //   1. Bail when the save fails. Before, the cook proceeded even if
  //      persist() returned null (unique-slug collision, RLS denial,
  //      network blip) — the user would cook something that never
  //      landed in their library, then wonder "where did my recipe go."
  //      Now we stop, re-toast, and let the user retry. The pushToast
  //      inside persist() already surfaced the original error; this
  //      extra nudge explains why cook didn't happen.
  //
  //   2. Mirror handleSchedule and stamp shared=true. Cooking an AI
  //      recipe means the rest of the family sees the cook_log and
  //      any scheduled_meals row in realtime. Without shared=true,
  //      the user_recipes row is private, the family view resolves
  //      null, and the meal detail drawer renders a "locked recipe"
  //      state. Cooking is a family-visible action; the recipe that
  //      was cooked should be too.
  const handleSaveAndCook = (source) => async (recipe, opts = {}) => {
    const row = await persist(recipe, source, { ...opts, shared: true });
    if (!row) {
      pushToast("Save failed — couldn't start cook", { emoji: "⚠️", kind: "warn" });
      return;
    }
    // Use the stamped (DB-slugged) recipe so subsequent lookups via
    // findRecipe hit the exact row we just wrote. Pre-save slug may
    // have collided and been suffixed "-2" — the raw `recipe` arg
    // still has the old slug.
    startCooking(row.recipe || recipe);
  };

  // Silent save — persist without toasting or closing. Used by the
  // compose-a-meal flow in AIRecipe: when the user clicks "+ Add
  // Side" from the main's preview, we persist the main first (so it's
  // a real user_recipes row) before re-entering setup for the side.
  // Returns the stamped recipe (with db-assigned slug) so the caller
  // can pin it into mealInProgress by its saved identity.
  const handleSilentSave = (source) => async (recipe, opts = {}) => {
    const row = await persist(recipe, source, opts);
    return row?.recipe || null;
  };

  // CookMode is mounted at the App level now (see App.jsx
  // cookModeRecipe state). The old inline-CookMode block that lived
  // here was removed when we pinned a resume banner to the top of
  // the app — keeping the cook alive across navigation means CookMode
  // cannot be tied to the CreateMenu overlay's lifetime.

  if (mode === "custom") {
    return (
      <div style={OVERLAY_STYLE}>
        <CustomRecipeBuilder
          pantry={pantry}
          onCancel={backToChoose}
          onSave={handleSave("custom")}
          onSchedule={handleSchedule("custom")}
          onSaveAndCook={handleSaveAndCook("custom")}
        />
        {scheduling && (
          <SchedulePicker
            recipe={scheduling}
            userId={userId}
            userName={userName}
            family={family}
            friends={friends}
            defaultRequest={hasFamily}
            onClose={() => setScheduling(null)}
            onSave={async ({ scheduledFor, notificationSettings, note, cookId, isRequest, servings }) => {
              try {
                await schedule({
                  recipeSlug: scheduling.slug,
                  // Stamp the author on scheduled_meals (migration 0139)
                  // so the cook-time resolver picks this user's row out
                  // of the shared (self + family) slug pool — otherwise
                  // a same-slug family recipe can shadow it at cook time.
                  // The save just before setScheduling() landed the row
                  // under this userId, so it's the right author here.
                  recipeUserId: userId,
                  scheduledFor,
                  notificationSettings,
                  note,
                  cookId,
                  isRequest,
                  servings,
                });
                setScheduling(null);
                pushToast("Scheduled — see the calendar tab", { emoji: "📅", kind: "info" });
                onClose?.();
              } catch (e) {
                console.error("[createMenu] schedule (custom) failed:", e);
                pushToast(`Couldn't schedule: ${e?.message || "try again"}`, { emoji: "⚠️", kind: "warn" });
              }
            }}
          />
        )}
      </div>
    );
  }

  if (mode === "ai") {
    // CreateMenu is rendered inside the App's tab AnimatePresence
    // motion.div, which has `transform: translateY(...)` — that
    // creates a containing block and bounds any descendant
    // position:fixed inside it. Portal the AI sub-phase to body so
    // its OVERLAY_STYLE actually covers the viewport. Same pattern
    // ShopModeCheckout / FloatingLocationDock / SchedulePicker use.
    if (typeof document === "undefined") return null;
    return createPortal((
      <div style={OVERLAY_STYLE}>
        <AIRecipe
          pantry={aiPantryOverride || (kitchenTracking ? pantry : [])}
          profile={profile}
          cookLogs={cookLogs}
          ingredientInfo={ingredientInfo}
          userRecipes={userRecipes}
          bundledRecipes={RECIPES}
          initialMealPrompt={aiMealPromptSeed}
          photoSeedItems={aiPantryOverride}
          photoSeedSketch={aiSketchOverride}
          onCancel={() => { setAiPantryOverride(null); setAiMealPromptSeed(null); setAiSketchOverride(null); backToChoose(); }}
          onSave={handleSave("ai")}
          onSilentSave={handleSilentSave("ai")}
          onSchedule={handleSchedule("ai")}
          onSaveAndCook={handleSaveAndCook("ai")}
          onBackgroundGenerate={onBackgroundGenerate}
          onMealSave={async (payload) => {
            try {
              await createMeal(payload);
              pushToast("Meal saved", { emoji: "🍽️", kind: "info" });
            } catch (e) {
              console.error("[createMenu] createMeal failed:", e);
              pushToast("Couldn't save meal", { emoji: "⚠️", kind: "warn" });
            }
          }}
          onAddToPantry={onAIAddToPantry}
          onCookExistingRecipe={(recipe) => {
            // Community dedupe — user picked an existing community
            // recipe from the AIRecipe pre-gen match phase. Skip the
            // generate-and-save dance; route straight to CookMode with
            // the community attribution preserved on the recipe object
            // so CookComplete stamps cook_logs.community_recipe_id.
            startCooking(recipe);
          }}
          onShoppingAdd={(items) => {
            // Items come in as { name, amount, unit, ingredientId,
            // source: "ai-recipe" }. Merge into shoppingList with
            // fresh uuids so useSyncedList persists them.
            //
            // Canonical backfill: AIRecipe forwards `ingredientId`
            // only when Claude stamped it on the recipe ingredient.
            // Claude leaves it null for "staples it assumed" and any
            // newly-introduced ingredient (generate-recipe/index.ts
            // :447). Before we push to the list, run a substring
            // lookup against the bundled registry so a match like
            // "ricotta cheese" → ricotta lands with the canonical
            // stamped — enabling the +30 receipt-scan bias tier
            // instead of the weaker +20 free-text tier.
            if (!items || items.length === 0) return;
            setShoppingList(prev => [
              ...prev,
              ...items.map(i => {
                const resolvedId = i.ingredientId
                  || inferCanonicalFromName(i.name)
                  || null;
                const canonical = findIngredient(resolvedId);
                return {
                  id: (typeof crypto !== "undefined" && crypto.randomUUID)
                    ? crypto.randomUUID()
                    : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                  name: canonical?.name || i.name,
                  emoji: canonical?.emoji || "🥫",
                  amount: i.amount,
                  unit: i.unit,
                  ingredientId: resolvedId,
                  category: canonical?.category || "pantry",
                  source: "ai-recipe",
                };
              }),
            ]);
          }}
        />
        {scheduling && (
          <SchedulePicker
            recipe={scheduling}
            userId={userId}
            userName={userName}
            family={family}
            friends={friends}
            defaultRequest={hasFamily}
            onClose={() => setScheduling(null)}
            onSave={async ({ scheduledFor, notificationSettings, note, cookId, isRequest, servings }) => {
              try {
                await schedule({
                  recipeSlug: scheduling.slug,
                  // Author stamp — see the custom-mode callback above.
                  // Without this, scheduled_meals.recipe_user_id is null
                  // and the cook-time resolver can't tell this row's
                  // ingredients from a family member's same-slug recipe.
                  recipeUserId: userId,
                  scheduledFor,
                  notificationSettings,
                  note,
                  cookId,
                  isRequest,
                  servings,
                });
                setScheduling(null);
                pushToast("Scheduled — see the calendar tab", { emoji: "📅", kind: "info" });
                onClose?.();
              } catch (e) {
                console.error("[createMenu] schedule (ai) failed:", e);
                pushToast(`Couldn't schedule: ${e?.message || "try again"}`, { emoji: "⚠️", kind: "warn" });
              }
            }}
          />
        )}
      </div>
    ), document.body);
  }

  // The addIntent sub-phase was deleted — scan + manual are now top-
  // level cards on the main chooser (one tap, not two). onRequestPantryAction
  // is still the dispatch path; the chooser cards call it directly.

  if (mode === "template") {
    const totalResults =
      filteredMeals.length + filteredUserCustom.length + filteredUserAI.length + filteredFamilyShared.length + filteredBundled.length;
    return (
      <div style={OVERLAY_STYLE}>
        <div style={{ padding: "24px 20px 20px", display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${COLOR.edge}` }}>
          <button onClick={backToChoose} style={iconBtn}>←</button>
          <div style={{ flex: 1, fontFamily: FONT.mono, fontSize: 10, color: COLOR.gold, letterSpacing: "0.12em" }}>
            PICK A RECIPE
          </div>
          <button onClick={onClose} style={iconBtn}>✕</button>
        </div>
        <div style={{ padding: "14px 20px 0" }}>
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search your recipes and the library…"
            style={{
              width: "100%", padding: "12px 14px",
              background: COLOR.soil, border: `1px solid ${COLOR.border}`,
              borderRadius: 10, color: COLOR.ink, boxSizing: "border-box",
              fontFamily: FONT.sans, fontSize: 14, outline: "none",
            }}
          />
        </div>
        <div style={{ padding: "14px 20px 100px" }}>
          {filteredMeals.length > 0 && (
            <RecipeSection title="YOUR MEALS" accent={TAG_MEAL}>
              {filteredMeals.map(m => (
                <MealRow
                  key={m.id}
                  meal={m}
                  onClick={() => setViewingMeal(m)}
                />
              ))}
            </RecipeSection>
          )}
          {filteredUserCustom.length > 0 && (
            <RecipeSection title="YOUR RECIPES" accent={TAG_CUSTOM}>
              {filteredUserCustom.map(ur => (
                <RecipeRow
                  key={ur.id}
                  recipe={ur.recipe}
                  tag="CUSTOM"
                  tagColor={TAG_CUSTOM}
                  onClick={() => startCooking(ur.recipe)}
                />
              ))}
            </RecipeSection>
          )}
          {filteredUserAI.length > 0 && (
            <RecipeSection title="AI DRAFTS" accent={TAG_AI}>
              {filteredUserAI.map(ur => (
                <RecipeRow
                  key={ur.id}
                  recipe={ur.recipe}
                  tag="AI"
                  tagColor={TAG_AI}
                  onClick={() => startCooking(ur.recipe)}
                />
              ))}
            </RecipeSection>
          )}
          {/* FAMILY SHARED — recipes another family member flipped
              shared=true on. The viewer can cook them the same way
              as their own; author's first name rides as the row tag
              ("MARISSA") so it's obvious whose recipe it is. */}
          {filteredFamilyShared.length > 0 && (
            <RecipeSection title="SHARED BY FAMILY" accent={COLOR.leafMuted}>
              {filteredFamilyShared.map(ur => (
                <RecipeRow
                  key={ur.id}
                  recipe={ur.recipe}
                  tag={authorNameFor(ur.userId).toUpperCase()}
                  tagColor={COLOR.leafMuted}
                  onClick={() => startCooking(ur.recipe)}
                />
              ))}
            </RecipeSection>
          )}
          {filteredBundled.length > 0 && (
            <RecipeSection title="BUNDLED TEMPLATES" accent={COLOR.sky}>
              {filteredBundled.map(r => (
                <RecipeRow
                  key={r.slug}
                  recipe={r}
                  tag={null}
                  onClick={() => startCooking(r)}
                />
              ))}
            </RecipeSection>
          )}
          {totalResults === 0 && (
            <div style={{ padding: 30, textAlign: "center", color: COLOR.sub, fontFamily: FONT.sans, fontSize: 13 }}>
              {query ? `No recipes match "${query}"` : "No recipes yet."}
            </div>
          )}
        </div>
        {viewingMeal && (
          <MealDetail
            meal={viewingMeal}
            pantry={pantry}
            ingredientInfo={ingredientInfo}
            brandNutrition={brandNutrition}
            onClose={() => setViewingMeal(null)}
            onCookPiece={(pieceRecipe) => {
              // Tap a piece inside the meal detail to cook just that
              // piece. Phase 3 doesn't sequence multiple pieces — each
              // component cooks individually. "Cook whole meal" is a
              // future ticket (CookMode needs a multi-recipe mode).
              setViewingMeal(null);
              startCooking(pieceRecipe);
            }}
            onDelete={async () => {
              try {
                await deleteMeal(viewingMeal.id);
                setViewingMeal(null);
                pushToast("Meal removed (recipes kept)", { emoji: "🗑️", kind: "info" });
              } catch (e) {
                console.error("[createMenu] deleteMeal failed:", e);
                pushToast("Couldn't remove meal", { emoji: "⚠️", kind: "warn" });
              }
            }}
          />
        )}
      </div>
    );
  }

  // Default — the chooser. Cards grouped into QUICK COOK (AI draft,
  // library, custom builder) and QUICK KITCHEN ADD (scan, manual).
  // Order within QUICK COOK: AI first (the new / featured path —
  // user directive), pick-a-recipe second (library when you know
  // what you want), custom last (authoring from scratch). Scan is
  // top of the add group since it's faster for packaged goods;
  // manual is the fallback. No sub-phases — every card is one tap
  // from the chooser to the action.
  return (
    // Rendered inline as a tab page (App.jsx routes `tab === "create"`
    // here). No overlay chrome — the App tab AnimatePresence handles
    // enter/exit cross-fades, and the WarmBackdrop paints the cream
    // parchment surface every page sits on.
    <div style={PAGE_STYLE}>
      {/* Bottom padding clears the App-level dock pill (~64px from
          bottom + safe-area). 100px gives the last card room. */}
      {/* Top bar — kicker + close. Top padding includes safe-area-
          inset-top so the chrome clears the iOS Dynamic Island.
          Close button is a 44pt tap target with explicit "Done"
          label so escape is unambiguous. */}
      <div style={{
        padding: "calc(16px + env(safe-area-inset-top)) 16px 0",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 12,
      }}>
        <div style={{
          fontFamily: FONT.forma, fontSize: 10, fontWeight: 600,
          color: COLOR.gold, letterSpacing: "0.16em",
          textTransform: "uppercase",
        }}>
          Create
        </div>
        {/* Top-right slot — pantry summary chip. Replaces the old
            close button (the goo dock indicator + tab nav give the
            user a clear path back; the chip shows what they have to
            work with so the choose decisions on this page are
            grounded in their actual kitchen state). */}
        {kitchenTracking && pantry && pantry.length > 0 && (
          <div style={{
            display: "inline-flex", alignItems: "baseline", gap: 6,
            fontFamily: FONT.forma,
            fontSize: 12,
            fontWeight: 600,
            color: COLOR.muted,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            lineHeight: 1,
          }}>
            <span style={{
              fontSize: 18,
              fontFamily: FONT.forma,
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              color: COLOR.gold,
              letterSpacing: "0.02em",
            }}>
              {pantry.length}
            </span>
            in kitchen
          </div>
        )}
      </div>

      {/* Page title — uses the shared `pageTitle` token + theme-aware
          skyInk color so it matches Home + Kitchen + Calendar
          headlines exactly (font/size/shadow/letterspacing). */}
      <div style={{ padding: "18px 20px 0" }}>
        <h1 style={{
          ...pageTitle,
          color: mcmTheme?.color?.skyInk || mcmTheme?.color?.ink || COLOR.ink,
        }}>
          Quick Create
        </h1>
      </div>

      <div style={{
        padding: "22px 20px calc(40px + env(safe-area-inset-bottom))",
        display: "flex", flexDirection: "column", gap: 10,
      }}>
        <SectionDivider label="Quick cook" />

        <button
          type="button"
          aria-label="Cook with Chadwick — AI drafts a recipe from your pantry"
          onClick={() => setMode("ai")}
          style={{
            display: "block",
            width: "100%",
            padding: 0,
            border: "none",
            boxShadow: `inset 0 0 0 1px ${COLOR.edge}`,
            borderRadius: 16,
            overflow: "hidden",
            position: "relative",
            cursor: "pointer",
            backgroundColor: COLOR.ground,
            backgroundImage: "url(/img/ai-cooking-hero.webp)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            aspectRatio: "16 / 5",
            minHeight: 120,
            textAlign: "left",
          }}
        >
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.55) 100%)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: 18,
              right: 18,
              bottom: 16,
              display: "flex",
              flexDirection: "column",
              gap: 0,
            }}
          >
            <div
              style={{
                fontFamily: FONT.itemTitle,
                fontSize: 22,
                fontWeight: 500,
                fontStyle: "italic",
                lineHeight: 1.05,
                color: "#fff",
                textShadow: "0 2px 8px rgba(0,0,0,0.45)",
              }}
            >
              Cook with Chadwick.
            </div>
          </div>
        </button>
        <button
          type="button"
          aria-label="Cook with your saved recipes"
          onClick={() => {
            if (onOpenRecipeBook) onOpenRecipeBook();
            else setMode("template");
          }}
          style={{
            display: "block",
            width: "100%",
            padding: 0,
            border: "none",
            boxShadow: `inset 0 0 0 1px ${COLOR.edge}`,
            borderRadius: 16,
            overflow: "hidden",
            position: "relative",
            cursor: "pointer",
            backgroundColor: COLOR.ground,
            backgroundImage: "url(/img/cooking-hero.webp)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            aspectRatio: "16 / 5",
            minHeight: 120,
            textAlign: "left",
          }}
        >
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.55) 100%)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: 18,
              right: 18,
              bottom: 16,
              display: "flex",
              flexDirection: "column",
              gap: 0,
            }}
          >
            <div
              style={{
                fontFamily: FONT.itemTitle,
                fontSize: 22,
                fontWeight: 500,
                fontStyle: "italic",
                lineHeight: 1.05,
                color: "#fff",
                textShadow: "0 2px 8px rgba(0,0,0,0.45)",
              }}
            >
              Cook with your saved recipes.
            </div>
          </div>
        </button>
        <button
          type="button"
          aria-label="Add a recipe — from a photo, video link, or pasted text"
          onClick={() => setAddRecipeOpen(true)}
          style={{
            display: "block",
            width: "100%",
            padding: 0,
            border: "none",
            boxShadow: `inset 0 0 0 1px ${COLOR.edge}`,
            borderRadius: 16,
            overflow: "hidden",
            position: "relative",
            cursor: "pointer",
            backgroundColor: COLOR.ground,
            aspectRatio: "16 / 5",
            minHeight: 120,
            textAlign: "left",
          }}
        >
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(135deg, rgba(212,166,55,0.18) 0%, rgba(47,143,131,0.14) 100%)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: 18,
              right: 18,
              bottom: 16,
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <div style={{
              fontFamily: FONT.forma, fontSize: 9, fontWeight: 600,
              color: COLOR.gold, letterSpacing: "0.16em",
              textTransform: "uppercase",
            }}>
              📸  ·  🔗  ·  📝
            </div>
            <div
              style={{
                fontFamily: FONT.itemTitle,
                fontSize: 22,
                fontWeight: 500,
                fontStyle: "italic",
                lineHeight: 1.05,
                color: COLOR.ink,
              }}
            >
              Add a recipe.
            </div>
          </div>
        </button>
        <button
          type="button"
          aria-label="Plan your week — AI schedules every meal and builds the shopping list"
          onClick={() => setPlannerOpen(true)}
          style={{
            display: "block",
            width: "100%",
            padding: 0,
            border: "none",
            boxShadow: `inset 0 0 0 1px ${COLOR.edge}`,
            borderRadius: 16,
            overflow: "hidden",
            position: "relative",
            cursor: "pointer",
            backgroundColor: COLOR.ground,
            aspectRatio: "16 / 5",
            minHeight: 120,
            textAlign: "left",
          }}
        >
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(135deg, rgba(212,166,55,0.22) 0%, rgba(111,175,155,0.16) 100%)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: 18,
              right: 18,
              bottom: 16,
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <div style={{
              fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
              color: COLOR.gold, letterSpacing: "0.16em",
              textTransform: "uppercase",
            }}>
              Breakfast · lunch · dinner, all seven days
            </div>
            <div
              style={{
                fontFamily: FONT.itemTitle,
                fontSize: 22,
                fontWeight: 500,
                fontStyle: "italic",
                lineHeight: 1.05,
                color: COLOR.ink,
              }}
            >
              Plan your week.
            </div>
          </div>
        </button>
        <SectionDivider label="Quick kitchen add" />

        {/* ONE add card. The old sibling pair ("Add to Kitchen." /
            "Add to shopping list.") looked identical and routed to
            different tables — a top source of add-flow confusion.
            The wizard's shared review surface now carries BOTH
            destination CTAs, so the split card is redundant; quick
            text jotting (ShoppingQuickAdd) lives on the shopping
            screen's JOT button instead. */}
        <button
          type="button"
          aria-label="Add items — kitchen or shopping list"
          onClick={() => {
            if (onOpenAddDraft) onOpenAddDraft();
            else if (onRequestPantryAction) onRequestPantryAction("add");
            else onClose?.();
          }}
          style={{
            display: "block",
            width: "100%",
            padding: 0,
            border: "none",
            boxShadow: `inset 0 0 0 1px ${COLOR.edge}`,
            borderRadius: 16,
            overflow: "hidden",
            position: "relative",
            cursor: "pointer",
            backgroundColor: COLOR.ground,
            backgroundImage: "url(/img/add-to-kitchen-hero.webp)",
            backgroundSize: "120%",
            backgroundPosition: "center 20%",
            backgroundRepeat: "no-repeat",
            aspectRatio: "16 / 5",
            minHeight: 120,
            textAlign: "right",
          }}
        >
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.55) 100%)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: 18,
              right: 18,
              bottom: 16,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 0,
            }}
          >
            <div
              style={{
                fontFamily: FONT.itemTitle,
                fontSize: 22,
                fontWeight: 500,
                fontStyle: "italic",
                lineHeight: 1.05,
                color: "#fff",
                textShadow: "0 2px 8px rgba(0,0,0,0.45)",
                textAlign: "right",
              }}
            >
              Add items.
            </div>
            <div
              style={{
                fontFamily: FONT.forma,
                fontSize: 12,
                color: "rgba(255,255,255,0.85)",
                textShadow: "0 1px 6px rgba(0,0,0,0.5)",
                textAlign: "right",
                marginTop: 2,
              }}
            >
              Kitchen or shopping list — you pick at the end.
            </div>
          </div>
        </button>
        {/* Eat — quick calorie log, no inventory tracking. Sits below
            kitchen + shopping for users who don't want to manage stock
            but still want the calorie ledger. Flat card (no hero image
            asset yet) so it reads as a quick utility instead of a
            primary planning surface. */}
        <button
          type="button"
          aria-label="Eat — log calories without managing inventory"
          onClick={() => {
            if (onOpenEatQuick) onOpenEatQuick();
            else onClose?.();
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            width: "100%",
            padding: "18px 22px",
            border: "none",
            boxShadow: `inset 0 0 0 1px ${COLOR.edge}`,
            borderRadius: 16,
            cursor: "pointer",
            backgroundColor: COLOR.deep,
            textAlign: "left",
          }}
        >
          <span style={{ fontSize: 32, lineHeight: 1 }}>🍽️</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontFamily: FONT.itemTitle,
                fontSize: 22,
                fontWeight: 500,
                fontStyle: "italic",
                lineHeight: 1.05,
                color: COLOR.ink,
              }}
            >
              Eat.
            </div>
            <div
              style={{
                fontFamily: FONT.mono,
                fontSize: 11,
                color: COLOR.sub,
                letterSpacing: 0.5,
                marginTop: 4,
                textTransform: "uppercase",
              }}
            >
              Quick log · no inventory
            </div>
          </div>
        </button>
      </div>

      {/* AddRecipeFromSheet — the universal "import a recipe" hub.
          Three pipelines inside (photo of ingredients → AI draft;
          video link + caption → parse-recipe-text; paste text →
          parse-recipe-text). Photo mode hands the identified
          ingredients up via onPhotoSeed; we use them as the AIRecipe
          pantry override and switch to the AI sub-phase. Link/text
          modes save through the normal user_recipes path with
          source="custom" and toast on success. */}
      {plannerOpen && (
        <WeeklyPlanner
          userId={userId}
          profile={profile}
          pantry={pantry}
          shoppingList={shoppingList}
          setShoppingList={setShoppingList}
          userRecipes={userRecipes}
          findUserRecipe={findUserRecipe}
          familyCount={family.length}
          onClose={() => setPlannerOpen(false)}
        />
      )}
      <AddRecipeFromSheet
        userId={userId}
        open={addRecipeOpen}
        initialLink={sharedLinkSeed}
        onInitialLinkConsumed={() => setSharedLinkSeed(null)}
        onClose={() => { setAddRecipeOpen(false); setSharedLinkSeed(null); }}
        onSave={async (recipe) => {
          const row = await persist(recipe, "custom");
          if (!row) return;
          pushToast("Saved to your recipes", { emoji: "💾", kind: "info" });
          setAddRecipeOpen(false);
          onClose?.();
        }}
        onPhotoSeed={(items) => {
          // Map identified ingredients → pantry-shaped rows. amount/unit
          // are intentionally null: we don't know quantities from a
          // photo, only identities. AIRecipe / generate-recipe handle
          // null amounts gracefully (treats as "available, unknown qty").
          const seed = items.map((it, i) => {
            const ing = it.canonicalId ? findIngredient(it.canonicalId) : null;
            return {
              id: `photo-seed-${Date.now()}-${i}`,
              name: ing?.name || it.name,
              canonicalId: it.canonicalId || null,
              amount: null,
              unit: null,
              category: ing?.category || "other",
              emoji: it.emoji || ing?.emoji || "🍽️",
              state: it.state || null,
            };
          });
          setAiPantryOverride(seed);
          setAddRecipeOpen(false);
          setMode("ai");
        }}
      />
    </div>
  );
}

function SectionDivider({ label }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      margin: "10px 0 4px",
    }}>
      <div style={{
        fontFamily: FONT.forma, fontSize: 9, fontWeight: 600,
        color: COLOR.muted, letterSpacing: "0.18em",
        textTransform: "uppercase",
      }}>
        {label}
      </div>
      <div style={{ flex: 1, height: 1, background: COLOR.edge }} />
    </div>
  );
}

// BigCard moved to src/components/MCMBigCard.jsx so Home (and any
// future caller) can reuse the same visual. Local alias kept so the
// existing call sites in this file don't churn.
const BigCard = MCMBigCard;

// Section wrapper used by the PICK A RECIPE list to visually separate
// YOUR RECIPES / AI DRAFTS / BUNDLED TEMPLATES. Hidden automatically by
// the caller when the section would be empty.
function RecipeSection({ title, accent, children }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{
        fontFamily: FONT.mono, fontSize: 10,
        color: accent, letterSpacing: "0.12em", marginBottom: 8,
      }}>
        {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {children}
      </div>
    </div>
  );
}

function RecipeRow({ recipe, tag, tagColor, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "12px 14px", background: COLOR.soil,
        border: `1px solid ${COLOR.border}`, borderRadius: 12,
        cursor: "pointer", textAlign: "left",
      }}
    >
      <div style={{ fontSize: 26, flexShrink: 0 }}>{recipe.emoji || "🍽️"}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            fontFamily: FONT.serif, fontSize: 15, color: COLOR.ink,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            flex: 1, minWidth: 0,
          }}>
            {recipe.title}
          </div>
          {tag && (
            <span style={{
              fontFamily: FONT.mono, fontSize: 8, fontWeight: 700,
              color: tagColor, background: `${tagColor}15`,
              border: `1px solid ${tagColor}55`,
              padding: "2px 6px", borderRadius: 6,
              letterSpacing: "0.1em", flexShrink: 0,
            }}>
              {tag}
            </span>
          )}
        </div>
        <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.sub, letterSpacing: "0.05em", marginTop: 2 }}>
          {(recipe.cuisine || "").toUpperCase()} · {totalTimeMin(recipe)} MIN · {difficultyLabel(recipe.difficulty).toUpperCase()}
        </div>
        {(recipe.course || recipe.mealTiming) && (
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 4 }}>
            {recipe.course     && <MetaPill label={recipe.course} />}
            {recipe.mealTiming && <MetaPill label={recipe.mealTiming} />}
          </div>
        )}
      </div>
      <span style={{ color: COLOR.gold, fontFamily: FONT.mono, fontSize: 14 }}>→</span>
    </button>
  );
}

// Mirror of AIRecipe.MetaPill. Neutral styling keeps clear of the
// reserved color axes in CLAUDE.md while still reading as metadata.
function MetaPill({ label }) {
  return (
    <span style={{
      fontFamily: FONT.mono, fontSize: 9, fontWeight: 700,
      color: COLOR.dim, background: COLOR.soil,
      border: `1px solid ${COLOR.border}`,
      padding: "2px 7px", borderRadius: 6,
      letterSpacing: "0.1em", textTransform: "uppercase",
    }}>
      {label}
    </span>
  );
}

// Tag colors per CLAUDE.md palette. CUSTOM uses the canonical-identity
// tan (user-authored identity); AI uses the state-axis purple (mirrors
// the AI-recipe card accent); MEAL uses a soft cream-white since
// "meal" is a higher-order concept (a bundle) and none of the reserved
// axis colors fit — green was the obvious third but it's reserved for
// the pantry accent elsewhere in the app.
const TAG_CUSTOM = CHIP_TONES.canonical.fg;
const TAG_AI     = COLOR.plum;
const TAG_MEAL   = "#e0d4b8";

// Row for a composed MEAL in the PICK A RECIPE list. Visually heavier
// than a single-recipe row so the user sees at a glance this is a
// bundle, not a recipe. Shows piece count + per-piece emoji stack.
function MealRow({ meal, onClick }) {
  const pieceEmojis = (meal.pieces || [])
    .slice(0, 4)
    .map(p => p.recipe?.emoji || "🍽️");
  const pieceCount = (meal.pieces || []).length;
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "12px 14px",
        background: COLOR.ground,
        border: `1px solid ${TAG_MEAL}33`,
        borderRadius: 12, cursor: "pointer", textAlign: "left",
      }}
    >
      <div style={{ fontSize: 26, flexShrink: 0 }}>{meal.emoji || "🍽️"}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            fontFamily: FONT.serif, fontSize: 15, color: COLOR.ink,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            flex: 1, minWidth: 0,
          }}>
            {meal.name}
          </div>
          <span style={{
            fontFamily: FONT.mono, fontSize: 8, fontWeight: 700,
            color: TAG_MEAL, background: `${TAG_MEAL}15`,
            border: `1px solid ${TAG_MEAL}55`,
            padding: "2px 6px", borderRadius: 6,
            letterSpacing: "0.1em", flexShrink: 0,
          }}>
            MEAL
          </span>
        </div>
        <div style={{
          fontFamily: FONT.mono, fontSize: 10, color: COLOR.sub,
          letterSpacing: "0.05em", marginTop: 2,
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <span>{pieceCount} PIECE{pieceCount === 1 ? "" : "S"}</span>
          {meal.cuisine && <span>· {meal.cuisine.toUpperCase()}</span>}
          {meal.mealTiming && <span>· {meal.mealTiming.toUpperCase()}</span>}
          <span style={{ marginLeft: "auto", fontSize: 13, letterSpacing: 0 }}>
            {pieceEmojis.join(" ")}
          </span>
        </div>
      </div>
      <span style={{ color: TAG_MEAL, fontFamily: FONT.mono, fontSize: 14 }}>→</span>
    </button>
  );
}

// Full-screen overlay showing a meal's pieces. Each piece is a
// tappable row that fires onCookPiece(pieceRecipe) — Phase 3 cooks
// individual pieces via the existing CookMode; a future "cook whole
// meal" mode would sequence the pieces. Includes a delete action
// that cascades only to meal_recipes (pieces stay in the library).
function MealDetail({ meal, pantry = [], ingredientInfo, brandNutrition, onClose, onCookPiece, onDelete }) {
  const pieceCount = (meal.pieces || []).length;
  // Sum of per-serving macros across each piece. Hidden when no piece
  // resolves (coverage.ingredients.resolved === 0) so we don't render
  // a misleading zero card on a meal where no nutrition data exists.
  const macros = useMemo(
    () => mealNutrition(meal, { pantry, getInfo: ingredientInfo?.getInfo, brandNutrition }),
    [meal, pantry, ingredientInfo, brandNutrition],
  );
  const showMacros = macros && macros.coverage.ingredients.resolved > 0;
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 220,
      background: COLOR.deep,
      maxWidth: 480, margin: "0 auto",
      overflowY: "auto",
    }}>
      <div style={{
        padding: "24px 20px 12px",
        display: "flex", alignItems: "center", gap: 10,
        borderBottom: `1px solid ${COLOR.edge}`,
      }}>
        <button onClick={onClose} style={iconBtn}>←</button>
        <div style={{
          flex: 1, fontFamily: FONT.mono, fontSize: 10,
          color: TAG_MEAL, letterSpacing: "0.12em",
        }}>
          MEAL DETAIL
        </div>
        <button onClick={onClose} style={iconBtn}>✕</button>
      </div>
      <div style={{ padding: "20px 20px 0", textAlign: "center" }}>
        <div style={{ fontSize: 46 }}>{meal.emoji || "🍽️"}</div>
        <h1 style={{
          fontFamily: FONT.serif, fontSize: 24, fontWeight: 300,
          fontStyle: "italic", color: COLOR.ink, margin: "8px 0 2px",
        }}>
          {meal.name}
        </h1>
        <div style={{
          fontFamily: FONT.mono, fontSize: 10, color: COLOR.sub,
          letterSpacing: "0.1em",
        }}>
          {pieceCount} PIECE{pieceCount === 1 ? "" : "S"}
          {meal.cuisine && ` · ${meal.cuisine.toUpperCase()}`}
          {meal.mealTiming && ` · ${meal.mealTiming.toUpperCase()}`}
        </div>
        {/* Meal-level macros — sum of per-serving across pieces. One
            eater's share of the whole meal. Coverage string is honest
            when not every ingredient resolved. */}
        {showMacros && (
          <div style={{
            marginTop: 14, padding: "10px 14px",
            background: COLOR.ground, border: `1px solid ${COLOR.edge}`,
            borderRadius: 10,
          }}>
            <div style={{
              fontFamily: FONT.sans, fontSize: 13, color: COLOR.ink,
            }}>
              ~ {formatMacros(macros.total, { verbose: true })}
            </div>
            <div style={{
              marginTop: 3,
              fontFamily: FONT.mono, fontSize: 9,
              color: COLOR.muted, letterSpacing: "0.08em",
            }}>
              PER EATER
              {macros.coverage.ingredients.resolved < macros.coverage.ingredients.total
                ? ` · BASED ON ${macros.coverage.ingredients.resolved} OF ${macros.coverage.ingredients.total} INGREDIENTS`
                : ""}
            </div>
          </div>
        )}
      </div>
      <div style={{ padding: "20px 20px 100px" }}>
        <div style={{
          fontFamily: FONT.mono, fontSize: 10,
          color: TAG_MEAL, letterSpacing: "0.12em",
          marginBottom: 8,
        }}>
          PIECES
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {(meal.pieces || []).map((p, i) => {
            const r = p.recipe;
            const missing = !r;
            return (
              <button
                key={`${p.recipeSlug}-${i}`}
                onClick={() => !missing && onCookPiece(r)}
                disabled={missing}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "12px 14px",
                  background: missing ? "#1a1515" : COLOR.soil,
                  border: `1px solid ${missing ? "#3a2020" : COLOR.border}`,
                  borderRadius: 12,
                  cursor: missing ? "not-allowed" : "pointer",
                  textAlign: "left",
                  opacity: missing ? 0.65 : 1,
                }}
              >
                <div style={{ fontSize: 24, flexShrink: 0 }}>
                  {r?.emoji || "🍽️"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{
                      fontFamily: FONT.serif, fontSize: 14, color: COLOR.ink,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      flex: 1, minWidth: 0,
                    }}>
                      {r?.title || `(missing: ${p.recipeSlug})`}
                    </div>
                    <span style={{
                      fontFamily: FONT.mono, fontSize: 8, fontWeight: 700,
                      color: COLOR.dim, background: COLOR.soil,
                      border: `1px solid ${COLOR.border}`,
                      padding: "2px 6px", borderRadius: 6,
                      letterSpacing: "0.1em", textTransform: "uppercase",
                      flexShrink: 0,
                    }}>
                      {p.course}
                    </span>
                  </div>
                  {r && (
                    <div style={{
                      fontFamily: FONT.mono, fontSize: 10, color: COLOR.sub,
                      letterSpacing: "0.05em", marginTop: 2,
                    }}>
                      {(r.cuisine || "").toUpperCase()} · {totalTimeMin(r)} MIN
                    </div>
                  )}
                </div>
                {!missing && (
                  <span style={{ color: COLOR.gold, fontFamily: FONT.mono, fontSize: 12 }}>
                    COOK →
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <button
          onClick={onDelete}
          style={{
            marginTop: 24, width: "100%", padding: "12px",
            background: "transparent", border: "1px solid #3a2020",
            color: "#a06060", borderRadius: 10,
            fontFamily: FONT.mono, fontSize: 10, fontWeight: 600,
            letterSpacing: "0.1em", cursor: "pointer",
          }}
        >
          REMOVE MEAL · KEEP RECIPES
        </button>
      </div>
    </div>
  );
}

// Fullscreen overlay — must be OPAQUE so the underlying tab doesn't
// bleed through. COLOR.deep is a translucent cream alpha (used for
// in-flow card surfaces), which made the kitchen below visibly stack
// behind the menu. COLOR.night is the opaque page-fill cream.
const PAGE_STYLE = {
  // Plain tab page — the App.jsx tab AnimatePresence motion.div
  // already provides positioning + cross-fade animation; this just
  // renders content inside it. Transparent so the App-root
  // WarmBackdrop's cream parchment + decorative blobs read through.
  color: COLOR.ink,
};

// Legacy export kept for the sub-flow phases (custom / ai / template)
// that still take over the viewport. They use it directly. Once those
// are also migrated to tab pages, this can go.
const OVERLAY_STYLE = {
  position: "fixed", inset: 0, zIndex: 210,
  background: COLOR.night,
  maxWidth: 480, margin: "0 auto",
  overflowY: "auto",
  overscrollBehavior: "contain",
  WebkitOverflowScrolling: "touch",
  color: COLOR.ink,
};

const iconBtn = {
  background: "transparent",
  border: `1px solid ${COLOR.border}`,
  borderRadius: 16,
  width: 32, height: 32,
  color: COLOR.muted,
  fontFamily: "system-ui, sans-serif",
  fontSize: 18, fontWeight: 500,
  cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  lineHeight: 1,
};
